/* ZakelijkLoginPage - Zakelijke login + account aanvragen pagina met tabbladen.
   Updated: Initial creation - login tab + aanvraag tab voor zakelijke klanten.
   Updated: Added DataFast business_signup_submitted goal. */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader as Loader2, Lock, Mail, Building2, User, Phone, MapPin, FileText, CheckCircle2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { trackGoal } from '@/lib/datafast';

type TabType = 'login' | 'aanvragen';

export default function ZakelijkLoginPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('login');

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Aanvraag state
  const [formData, setFormData] = useState({
    company_name: '',
    contact_first_name: '',
    contact_last_name: '',
    email: '',
    phone: '',
    kvk_number: '',
    btw_number: '',
    country: 'Nederland',
    postal_code: '',
    street: '',
    house_number: '',
    city: '',
    terms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });

      if (authError) throw authError;

      if (authData.user) {
        const { data: businessData, error: businessError } = await supabase
          .from('business_customers')
          .select('id, status, must_reset_password')
          .eq('auth_user_id', authData.user.id)
          .maybeSingle();

        if (businessError) throw businessError;

        if (!businessData) {
          await supabase.auth.signOut();
          throw new Error('Geen zakelijk account gevonden');
        }

        if (businessData.status === 'pending') {
          await supabase.auth.signOut();
          throw new Error('Je account is nog niet goedgekeurd door Buitn Planten');
        }

        if (businessData.status === 'rejected') {
          await supabase.auth.signOut();
          throw new Error('Je aanvraag is afgewezen. Neem contact op met Buitn Planten.');
        }

        if (businessData.status === 'blocked') {
          await supabase.auth.signOut();
          throw new Error('Je account is geblokkeerd. Neem contact op met Buitn Planten.');
        }

        if (businessData.must_reset_password) {
          navigate('/zakelijk/wachtwoord-wijzigen');
          return;
        }

        navigate('/zakelijk');
      }
    } catch (err: any) {
      setLoginError(err.message || 'Inloggen mislukt');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.company_name.trim()) { toast.error('Vul je bedrijfsnaam in'); return; }
    if (!formData.contact_first_name.trim()) { toast.error('Vul je voornaam in'); return; }
    if (!formData.contact_last_name.trim()) { toast.error('Vul je achternaam in'); return; }
    if (!formData.email.trim() || !formData.email.includes('@')) { toast.error('Vul een geldig e-mailadres in'); return; }
    if (!formData.phone.trim()) { toast.error('Vul je telefoonnummer in'); return; }
    if (!formData.kvk_number.trim()) { toast.error('Vul je KVK-nummer in'); return; }
    if (!formData.postal_code.trim()) { toast.error('Vul je postcode in'); return; }
    if (!formData.street.trim()) { toast.error('Vul je straatnaam in'); return; }
    if (!formData.house_number.trim()) { toast.error('Vul je huisnummer in'); return; }
    if (!formData.city.trim()) { toast.error('Vul je plaats in'); return; }
    if (!formData.terms) { toast.error('Je moet akkoord gaan met de voorwaarden'); return; }

    setSubmitting(true);

    try {
      const { error } = await supabase.from('business_customers').insert({
        company_name: formData.company_name.trim(),
        contact_first_name: formData.contact_first_name.trim(),
        contact_last_name: formData.contact_last_name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        kvk_number: formData.kvk_number.trim(),
        btw_number: formData.btw_number.trim() || null,
        country: formData.country,
        postal_code: formData.postal_code.trim(),
        street: formData.street.trim(),
        house_number: formData.house_number.trim(),
        city: formData.city.trim(),
      });

      if (error) {
        if (error.code === '23505') {
          toast.error('Er is al een account met dit e-mailadres aangemeld');
        } else {
          toast.error('Er is een fout opgetreden. Probeer het opnieuw.');
        }
        return;
      }

      setSubmitted(true);
      toast.success('Aanvraag verzonden!');
      trackGoal('business_signup_submitted', { company_name: formData.company_name.trim() });
    } catch {
      toast.error('Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Zakelijk inloggen | Buitn Planten</title>
        <meta name="description" content="Zakelijk inloggen of een zakelijk account aanvragen bij Buitn Planten." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-[#fcfaf6] flex items-center justify-center p-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-12">
            <a href="/">
              <img
                src="/assets/logos/buitnplanten-logo-green.webp"
                alt="Buitn Planten"
                className="h-16 mx-auto mb-3"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </a>
          </div>

          {/* Tab buttons */}
          <div className="flex border-b border-[#e7e1d6] mb-6">
            <button
              onClick={() => { setActiveTab('login'); setLoginError(''); }}
              className={`flex-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === 'login'
                  ? 'text-[#1e2b22] border-b-2 border-[#1e2b22]'
                  : 'text-[#5a6552] hover:text-[#1e2b22]'
              }`}
            >
              Zakelijk inloggen
            </button>
            <button
              onClick={() => setActiveTab('aanvragen')}
              className={`flex-1 pb-3 text-sm font-medium transition-colors ${
                activeTab === 'aanvragen'
                  ? 'text-[#1e2b22] border-b-2 border-[#1e2b22]'
                  : 'text-[#5a6552] hover:text-[#1e2b22]'
              }`}
            >
              Account aanvragen
            </button>
          </div>

          <div className="bg-white/85 backdrop-blur-xl rounded-3xl shadow-xl border border-[#e7e1d6] overflow-hidden">
            {activeTab === 'login' && (
              <div className="p-8">
                {loginError && (
                  <Alert variant="destructive" className="mb-4 bg-red-50 border-red-200">
                    <AlertDescription className="text-red-800">{loginError}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="zakelijk-email" className="text-sm font-medium text-[#1e2b22]">
                      E-mailadres
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a6552]" />
                      <Input
                        id="zakelijk-email"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="bedrijf@voorbeeld.nl"
                        required
                        disabled={loginLoading}
                        className="pl-10 h-12 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22] focus:ring-[#1e2b22]/20"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="zakelijk-password" className="text-sm font-medium text-[#1e2b22]">
                      Wachtwoord
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a6552]" />
                      <Input
                        id="zakelijk-password"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        disabled={loginLoading}
                        className="pl-10 h-12 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22] focus:ring-[#1e2b22]/20"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-[#1e2b22] hover:bg-[#2a3a2f] text-white font-semibold shadow-lg shadow-[#1e2b22]/20 transition-all duration-300 hover:shadow-xl hover:scale-[1.01]"
                    disabled={loginLoading}
                  >
                    {loginLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Inloggen...
                      </>
                    ) : (
                      'Inloggen'
                    )}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-[#e7e1d6] text-center">
                  <p className="text-sm text-[#5a6552]">
                    Nog geen zakelijk account?{' '}
                    <button
                      onClick={() => setActiveTab('aanvragen')}
                      className="text-[#1e2b22] font-medium underline hover:no-underline"
                    >
                      Account aanvragen
                    </button>
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'aanvragen' && !submitted && (
              <div className="p-8">
                <p className="text-sm text-[#5a6552] mb-6">
                  Vul onderstaand formulier in. Na controle nemen wij contact met je op en ontvang je inloggegevens.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-sm font-medium text-[#1e2b22]">
                      Bedrijfsnaam <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a6552]" />
                      <Input
                        id="company_name"
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => handleInputChange('company_name', e.target.value)}
                        placeholder="Buitn B.V."
                        required
                        disabled={submitting}
                        className="pl-10 h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="contact_first_name" className="text-sm font-medium text-[#1e2b22]">
                        Voornaam <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contact_first_name"
                        type="text"
                        value={formData.contact_first_name}
                        onChange={(e) => handleInputChange('contact_first_name', e.target.value)}
                        placeholder="Jan"
                        required
                        disabled={submitting}
                        className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_last_name" className="text-sm font-medium text-[#1e2b22]">
                        Achternaam <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="contact_last_name"
                        type="text"
                        value={formData.contact_last_name}
                        onChange={(e) => handleInputChange('contact_last_name', e.target.value)}
                        placeholder="Jansen"
                        required
                        disabled={submitting}
                        className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-[#1e2b22]">
                      E-mailadres <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a6552]" />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="jan@buitn.nl"
                        required
                        disabled={submitting}
                        className="pl-10 h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-[#1e2b22]">
                      Telefoonnummer <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a6552]" />
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="06 12345678"
                        required
                        disabled={submitting}
                        className="pl-10 h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="kvk_number" className="text-sm font-medium text-[#1e2b22]">
                        KVK-nummer <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="kvk_number"
                        type="text"
                        value={formData.kvk_number}
                        onChange={(e) => handleInputChange('kvk_number', e.target.value)}
                        placeholder="12345678"
                        required
                        disabled={submitting}
                        className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="btw_number" className="text-sm font-medium text-[#1e2b22]">
                        BTW-nummer
                      </Label>
                      <Input
                        id="btw_number"
                        type="text"
                        value={formData.btw_number}
                        onChange={(e) => handleInputChange('btw_number', e.target.value)}
                        placeholder="NL123456789B01"
                        disabled={submitting}
                        className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-sm font-medium text-[#1e2b22]">
                      Land
                    </Label>
                    <Input
                      id="country"
                      type="text"
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      disabled={submitting}
                      className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="postal_code" className="text-sm font-medium text-[#1e2b22]">
                        Postcode <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="postal_code"
                        type="text"
                        value={formData.postal_code}
                        onChange={(e) => handleInputChange('postal_code', e.target.value)}
                        placeholder="1234 AB"
                        required
                        disabled={submitting}
                        className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="house_number" className="text-sm font-medium text-[#1e2b22]">
                        Huisnummer + toevoeging <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="house_number"
                        type="text"
                        value={formData.house_number}
                        onChange={(e) => handleInputChange('house_number', e.target.value)}
                        placeholder="12A"
                        required
                        disabled={submitting}
                        className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="street" className="text-sm font-medium text-[#1e2b22]">
                      Straatnaam <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="street"
                      type="text"
                      value={formData.street}
                      onChange={(e) => handleInputChange('street', e.target.value)}
                      placeholder="Dorpstraat"
                      required
                      disabled={submitting}
                      className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-sm font-medium text-[#1e2b22]">
                      Plaats <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="Dedemsvaart"
                      required
                      disabled={submitting}
                      className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] placeholder:text-[#cfcac2] focus:border-[#1e2b22]"
                    />
                  </div>

                  <div className="flex items-start space-x-3 pt-2">
                    <Checkbox
                      id="terms"
                      checked={formData.terms}
                      onCheckedChange={(checked) => handleInputChange('terms', checked === true)}
                    />
                    <label htmlFor="terms" className="text-sm leading-tight cursor-pointer text-[#5a6552]">
                      Ik ga akkoord met de{' '}
                      <a href="/algemene-voorwaarden" className="text-[#1e2b22] font-medium underline hover:no-underline">algemene voorwaarden</a>{' '}
                      en{' '}
                      <a href="/privacy" className="text-[#1e2b22] font-medium underline hover:no-underline">privacyverklaring</a>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 bg-[#1e2b22] hover:bg-[#2a3a2f] text-white font-semibold shadow-lg shadow-[#1e2b22]/20 transition-all duration-300 hover:shadow-xl hover:scale-[1.01]"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Versturen...
                      </>
                    ) : (
                      'Aanvraag versturen'
                    )}
                  </Button>
                </form>
              </div>
            )}

            {activeTab === 'aanvragen' && submitted && (
              <div className="p-10 text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-[#1e2b22] mb-3">Aanvraag ontvangen!</h2>
                  <p className="text-[#5a6552] mb-6">
                    Wij hebben je aanvraag ontvangen en zullen deze controleren. Na goedkeuring ontvang je een e-mail met je inloggegevens en tijdelijke wachtwoord.
                  </p>
                  <Button
                    onClick={() => { setSubmitted(false); setActiveTab('login'); }}
                    variant="outline"
                    className="border-[#e7e1d6] text-[#1e2b22] hover:bg-[#e7e1d6]/50"
                  >
                    Terug naar inloggen
                  </Button>
                </motion.div>
              </div>
            )}
          </div>

          <p className="text-center text-sm text-[#5a6552] mt-6">
            Buitn Planten © {new Date().getFullYear()}
          </p>
        </motion.div>
      </div>
    </>
  );
}
